import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkle } from "@phosphor-icons/react";

const MODES = [
  { v: "clarify", l: "Clarify the question" },
  { v: "structure", l: "Structure an answer" },
  { v: "relevant", l: "Find relevant experience" },
  { v: "improve", l: "Improve my draft" },
];

export default function Interview() {
  const [question, setQuestion] = useState("");
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState("structure");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!question.trim()) return;
    setLoading(true);
    setOutput("");
    try {
      const { data } = await api.post("/interview/help", { question, mode, draft });
      setOutput(data.content);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader title="Text pre-interview" subtitle="Practise role-related questions in text. Guidance uses only your verified profile — no invented experience, no personality scoring." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <Label>Interview question</Label>
          <Textarea className="mt-1.5" rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. Tell me about a time you coordinated a project across countries." data-testid="interview-question" />

          <Label className="mt-4 block">What do you need?</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {MODES.map((m) => (
              <button key={m.v} type="button" onClick={() => setMode(m.v)} data-testid={`interview-mode-${m.v}`}
                className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${mode === m.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                {m.l}
              </button>
            ))}
          </div>

          {mode === "improve" && (
            <div className="mt-4">
              <Label>Your draft answer</Label>
              <Textarea className="mt-1.5" rows={5} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Paste your draft answer to improve…" data-testid="interview-draft" />
            </div>
          )}

          <Button onClick={run} disabled={loading || !question.trim()} data-testid="interview-run-btn" className="mt-5 bg-slate-900 hover:bg-slate-800 gap-2">
            <Sparkle weight="fill" /> {loading ? "Thinking…" : "Get guidance"}
          </Button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <Label className="text-xs uppercase tracking-wide text-slate-500">Guidance</Label>
          {!output && !loading && <p className="mt-3 text-sm text-slate-400">Your guidance will appear here.</p>}
          {loading && <p className="mt-3 text-sm text-slate-400">Preparing guidance…</p>}
          {output && <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="interview-output">{output}</p>}
        </div>
      </div>
    </div>
  );
}
